import React, { useState, useEffect } from 'react';
import { X, Share2, Mail, Clock, Plus, Trash2, CheckCircle, XCircle, AlertTriangle, Loader2, UserCheck } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { toast } from 'react-hot-toast';

interface SimulatorInvitation {
  id: string;
  email: string;
  duration_days: number;
  expires_at: string | null;
  accepted_at: string | null;
  validated_at: string | null;
  user_id: string | null;
  created_at: string;
}

interface ShareSimulatorModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ShareSimulatorModal({ isOpen, onClose }: ShareSimulatorModalProps) {
  const [invitations, setInvitations] = useState<SimulatorInvitation[]>([]);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [durationDays, setDurationDays] = useState('30');
  const [creating, setCreating] = useState(false);
  const [actionId, setActionId] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) loadInvitations();
  }, [isOpen]);

  const loadInvitations = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('simulator_invitations')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      setInvitations(data || []);
    } catch {
      toast.error('Erreur lors du chargement des accès');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    const days = parseInt(durationDays);
    if (isNaN(days) || days < 1) {
      toast.error('Durée invalide');
      return;
    }

    setCreating(true);
    try {
      const { data: existing } = await supabase
        .from('simulator_invitations')
        .select('id')
        .eq('email', email.toLowerCase().trim())
        .maybeSingle();

      if (existing) {
        toast.error('Un accès existe déjà pour cet email.');
        return;
      }

      const { error } = await supabase
        .from('simulator_invitations')
        .insert({
          email: email.toLowerCase().trim(),
          duration_days: days,
        });

      if (error) throw error;

      toast.success(`Email ${email} autorisé pour le simulateur`);
      setEmail('');
      setDurationDays('30');
      await loadInvitations();
    } catch (err: any) {
      toast.error(err.message || 'Erreur lors de la création');
    } finally {
      setCreating(false);
    }
  };

  const handleValidate = async (inv: SimulatorInvitation) => {
    setActionId(inv.id);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { data: ok, error } = await supabase.rpc('validate_simulator_invitation', {
        p_email: inv.email,
        p_admin_id: user?.id,
      });
      if (error) throw error;
      if (!ok) throw new Error('Validation échouée');
      toast.success(`Accès validé pour ${inv.email}`);
      await loadInvitations();
    } catch (err: any) {
      toast.error(err.message || 'Erreur lors de la validation');
    } finally {
      setActionId(null);
    }
  };

  const handleDelete = async (id: string) => {
    setActionId(id);
    try {
      const { error } = await supabase
        .from('simulator_invitations')
        .delete()
        .eq('id', id);
      if (error) throw error;
      setInvitations(prev => prev.filter(inv => inv.id !== id));
      toast.success('Accès supprimé');
    } catch {
      toast.error('Erreur lors de la suppression');
    } finally {
      setActionId(null);
    }
  };

  const getStatus = (inv: SimulatorInvitation) => {
    if (!inv.user_id) return { label: 'En attente d\'inscription', color: 'bg-neutral-100 text-neutral-500' };
    if (!inv.validated_at) return { label: 'En attente de validation', color: 'bg-amber-100 text-amber-700' };
    if (!inv.expires_at) return { label: 'Actif', color: 'bg-emerald-100 text-emerald-700' };
    const now = new Date();
    const expires = new Date(inv.expires_at);
    if (expires < now) return { label: 'Expiré', color: 'bg-red-100 text-red-600' };
    const daysLeft = Math.ceil((expires.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return { label: `${daysLeft}j restants`, color: 'bg-emerald-100 text-emerald-700' };
  };

  const pendingValidation = invitations.filter(inv => inv.user_id && !inv.validated_at);
  const others = invitations.filter(inv => !(inv.user_id && !inv.validated_at));

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[85vh]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-100 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-[#F8BD3D] rounded-lg flex items-center justify-center">
              <Share2 className="w-4 h-4 text-neutral-900" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-neutral-900">Accès simulateur</h2>
              <p className="text-xs text-neutral-500">Gérez les accès au simulateur</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-neutral-100 rounded-lg transition-colors">
            <X className="w-4 h-4 text-neutral-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <p className="text-xs text-blue-700 font-medium mb-1">Comment ça fonctionne</p>
            <ol className="text-xs text-blue-700 space-y-1 list-decimal list-inside">
              <li>Ajoutez l'adresse email de l'utilisateur ci-dessous</li>
              <li>L'utilisateur crée son compte sur sunisup.be avec cet email exact (section "Accès simulateur")</li>
              <li>Validez le compte depuis cette page — l'accès est immédiatement activé pour la durée définie</li>
            </ol>
          </div>

          <form onSubmit={handleCreate} className="space-y-3">
            <p className="text-sm font-medium text-neutral-800">Autoriser un nouvel accès</p>
            <div className="flex gap-3">
              <div className="flex-1 relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-neutral-400" />
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="email@exemple.com"
                  required
                  className="w-full pl-9 pr-3 py-2.5 text-sm border border-neutral-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-300/50 focus:border-amber-300"
                />
              </div>
              <div className="relative w-36">
                <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-neutral-400" />
                <input
                  type="number"
                  min="1"
                  max="365"
                  value={durationDays}
                  onChange={e => setDurationDays(e.target.value)}
                  className="w-full pl-9 pr-10 py-2.5 text-sm border border-neutral-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-300/50 focus:border-amber-300"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-neutral-400">jours</span>
              </div>
              <button
                type="submit"
                disabled={creating}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm text-neutral-900 transition-all hover:opacity-90 disabled:opacity-60 shrink-0"
                style={{ backgroundColor: '#F8BD3D' }}
              >
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Ajouter
              </button>
            </div>
          </form>

          {pendingValidation.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <p className="text-sm font-medium text-neutral-800">En attente de validation</p>
                <span className="text-xs font-semibold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">{pendingValidation.length}</span>
              </div>
              <div className="space-y-2">
                {pendingValidation.map(inv => (
                  <div key={inv.id} className="px-4 py-3 rounded-xl border-2 border-amber-200 bg-amber-50">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-amber-200 flex items-center justify-center shrink-0">
                        <UserCheck className="w-3.5 h-3.5 text-amber-700" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-neutral-800 truncate">{inv.email}</p>
                        <p className="text-xs text-neutral-500">Inscrit · Accès {inv.duration_days} jours après validation</p>
                      </div>
                      <button
                        onClick={() => handleValidate(inv)}
                        disabled={actionId === inv.id}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-emerald-500 hover:bg-emerald-600 rounded-lg transition-colors disabled:opacity-60"
                      >
                        {actionId === inv.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3" />}
                        Valider
                      </button>
                      <button
                        onClick={() => handleDelete(inv.id)}
                        disabled={actionId === inv.id}
                        className="p-1.5 text-neutral-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <XCircle className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <p className="text-sm font-medium text-neutral-800 mb-3">Tous les accès</p>
            {loading ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="w-5 h-5 animate-spin text-neutral-300" />
              </div>
            ) : others.length === 0 && pendingValidation.length === 0 ? (
              <div className="text-center py-10 border-2 border-dashed border-neutral-200 rounded-xl">
                <Share2 className="w-8 h-8 text-neutral-300 mx-auto mb-2" />
                <p className="text-sm text-neutral-400">Aucun accès créé</p>
              </div>
            ) : (
              <div className="space-y-2">
                {others.map(inv => {
                  const status = getStatus(inv);
                  return (
                    <div key={inv.id} className="px-4 py-3 rounded-xl border border-neutral-200 bg-white hover:border-neutral-300 transition-colors">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-neutral-100 flex items-center justify-center shrink-0">
                          <Mail className="w-3.5 h-3.5 text-neutral-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-neutral-800 truncate">{inv.email}</p>
                          <p className="text-xs text-neutral-400">
                            {inv.duration_days} jours
                            {inv.expires_at && ` · expire le ${new Date(inv.expires_at).toLocaleDateString('fr-BE')}`}
                          </p>
                        </div>
                        <span className={`text-xs font-medium px-2.5 py-1 rounded-full shrink-0 ${status.color}`}>
                          {status.label}
                        </span>
                        <button
                          onClick={() => handleDelete(inv.id)}
                          disabled={actionId === inv.id}
                          className="p-1.5 text-neutral-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors shrink-0"
                        >
                          {actionId === inv.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
